/**
 * WaComposerFormattedPreview — overlay del textarea del composer.
 * Renderiza el texto con estilos WhatsApp (*negrita*, _cursiva_, ~tachado~,
 * `mono`, ```bloque```) pero CONSERVA los marcadores visibles, tal como
 * se ve en el input de WhatsApp mientras escribes.
 *
 * Pensado para colocarse detrás del `<textarea>` con `color: transparent`
 * para que el usuario vea el preview y escriba normal.
 */

import React, { Fragment } from 'react';
import { parseTokens, type WaToken } from './WaFormattedText';

const MONO_FONT =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';

function renderComposer(tokens: WaToken[], prefix = ''): React.ReactNode[] {
  return tokens.map((tok, i) => {
    const k = `${prefix}${i}`;
    switch (tok.type) {
      case 'text':
        return <Fragment key={k}>{tok.content}</Fragment>;
      case 'bold':
        return (
          <Fragment key={k}>
            *<strong>{renderComposer(parseTokens(tok.content), `${k}-`)}</strong>*
          </Fragment>
        );
      case 'italic':
        return (
          <Fragment key={k}>
            _<em>{renderComposer(parseTokens(tok.content), `${k}-`)}</em>_
          </Fragment>
        );
      case 'strike':
        return (
          <Fragment key={k}>
            ~<del>{renderComposer(parseTokens(tok.content), `${k}-`)}</del>~
          </Fragment>
        );
      case 'mono':
        return (
          <Fragment key={k}>
            `<span style={{ fontFamily: MONO_FONT }}>{tok.content}</span>`
          </Fragment>
        );
      case 'code':
        return (
          <Fragment key={k}>
            ```<span style={{ fontFamily: MONO_FONT }}>{tok.content}</span>```
          </Fragment>
        );
      default:
        return null;
    }
  });
}

interface Props {
  text: string;
}

export function WaComposerFormattedPreview({ text }: Props) {
  if (!text) return null;
  return <>{renderComposer(parseTokens(text))}</>;
}
